import { originPatternProblem, readCapturePattern } from '@vd/redaction/captureProfile';

/**
 * What a capture-profile address means, for the profile editor.
 *
 * The rule the extension and the API apply (`matchCaptureProfile` in
 * @vd/redaction) is small and easy to misread: a pattern is an exact address,
 * or - when it ends in `*` - every address that starts with it, at any depth.
 * A bare site address is exact, so `https://app.example.com` covered the home
 * page and nothing else, and a `*` anywhere but the end matched nothing.
 *
 * Everything here is read through the matcher's own reading of a pattern
 * (`readCapturePattern`) and its own list of what cannot be saved
 * (`originPatternProblem`). A first version described the text as typed, and
 * the matcher reads it canonical - host lowercased, default port, user name and
 * trailing dots gone - so what the editor said a rule covered and what it
 * covered could differ. What is saved is that canonical form, so the profile
 * list shows what each rule means.
 */

export type PatternReading =
  | { pattern: string; covers: string; note?: string }
  | { problem: string };

/** A pattern's canonical form, as saved: the text the matcher compares, and its star. */
function canonical(pattern: string): { saved: string; text: string; prefix: boolean } {
  const { text, prefix } = readCapturePattern(pattern);
  return { saved: prefix ? `${text}*` : text, text, prefix };
}

/** The origin when canonical text is exactly a site and its root, else null. */
function siteRoot(text: string): string | null {
  const match = /^(https?:\/\/[^/?#]+)\/$/i.exec(text);
  return match ? match[1]! : null;
}

/**
 * The whole site an address belongs to, as the pattern that covers it:
 * its origin followed by `/*`. A missing scheme is taken as https, and the
 * scheme is said, because http and https are two sites to the matcher.
 */
export function wholeSite(input: string): PatternReading {
  const text = input.trim();
  if (!text) return { problem: 'Enter the site, for example https://app.example.com.' };
  const typedScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(text);
  let url: URL;
  try {
    url = new URL(typedScheme ? text : `https://${text}`);
  } catch {
    return { problem: 'That is not a web address. Enter the site, for example https://app.example.com.' };
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return { problem: 'A capture profile covers http and https sites only.' };
  }
  if (url.hostname.includes('*')) {
    return { problem: 'A "*" cannot stand for part of the site name. Each subdomain needs its own profile.' };
  }
  const candidate = `${url.origin}/*`;
  const problem = originPatternProblem(candidate);
  if (problem) return { problem };
  const { saved, text: read } = canonical(candidate);
  const site = siteRoot(read) ?? url.origin;
  const typedMore = url.pathname !== '/' || url.search !== '' || url.hash !== '' || url.username !== '' || url.password !== '';
  const notes = [
    typedScheme ? undefined : `${url.protocol}// was assumed. A site served over http:// needs http:// here.`,
    typedMore ? 'Only the site is used, not the rest of what you typed. Untick "Cover the whole site" to limit the profile to part of it.' : undefined,
  ].filter(Boolean);
  return { pattern: saved, covers: `Every page on ${site}.`, ...(notes.length ? { note: notes.join(' ') } : {}) };
}

/** An address pattern as written, with any trailing run of stars read as one. */
export function readPattern(input: string): PatternReading {
  const typed = input.trim();
  if (!typed) return { problem: 'Enter an address pattern.' };
  // `/**` is how project URL patterns say "any depth". Here one `*` already
  // means that, so a trailing run is read as one rather than refused.
  const trailing = /\*+$/.exec(typed);
  const pattern = trailing ? `${typed.slice(0, trailing.index)}*` : typed;
  const problem = originPatternProblem(pattern);
  if (problem) return { problem };
  if (pattern === '*') return { pattern, covers: 'Every page of every site.' };
  const { saved, text, prefix } = canonical(pattern);
  const site = siteRoot(text);
  const notes: string[] = [];
  if (saved !== typed) {
    notes.push(trailing && trailing[0].length > 1
      ? `Saved as ${saved}: one "*" at the end already covers every level below it.`
      : `Saved as ${saved}.`);
  }
  if (!prefix) {
    if (site) notes.push('To cover every page on the site, tick "Cover the whole site".');
    return {
      pattern: saved,
      covers: site ? `Only the home page of ${site}.` : `Only ${text}, query string included.`,
      ...(notes.length ? { note: notes.join(' ') } : {}),
    };
  }
  if (site) return { pattern: saved, covers: `Every page on ${site}.`, ...(notes.length ? { note: notes.join(' ') } : {}) };
  // A prefix that stops inside the site name reaches other sites; one that
  // stops inside a path segment reaches its neighbours.
  if (/^https?:\/\/[^/?#]*$/i.test(text)) {
    notes.push(`This also covers other sites whose names start the same way, like ${text.replace(/^https?:\/\//i, '')}.other.net. End with "/*" to cover only this site.`);
  } else if (!text.endsWith('/') && !text.includes('?')) {
    notes.push(`This also covers addresses like ${text}-old or ${text}s. End with "/*" to stay inside ${text}/.`);
  }
  return {
    pattern: saved,
    covers: `Every address that starts with ${text}, at any depth.`,
    ...(notes.length ? { note: notes.join(' ') } : {}),
  };
}

/** The origin a stored pattern covers in full - its site and `/*` - or null. */
export function wholeSiteOrigin(pattern: string): string | null {
  if (originPatternProblem(pattern)) return null;
  const { text, prefix } = readCapturePattern(pattern);
  return prefix ? siteRoot(text) : null;
}

/** A short label for a stored pattern in the profile list, when it needs one. */
export function patternLabel(pattern: string): { text: string; tone: 'neutral' | 'warning' | 'danger' } | null {
  if (originPatternProblem(pattern)) {
    // A user name or a fragment still matches something, just not what it
    // says; everything else refused matches no page at all.
    return /[@#]/.test(pattern) ? { text: 'Needs fixing', tone: 'danger' } : { text: 'Matches nothing', tone: 'danger' };
  }
  const { text, prefix } = readCapturePattern(pattern);
  if (!siteRoot(text)) return null;
  return prefix ? { text: 'Whole site', tone: 'neutral' } : { text: 'Home page only', tone: 'warning' };
}
